/**
 * Regex-based intent detectors used by Coach Steve sub-agents.
 *
 * Each detector returns true when the user message clearly maps to the
 * given intent. They are intentionally conservative — when in doubt we
 * fall through to the general-question path.
 *
 * Phase B will replace these with an LLM router; in Phase A they back the
 * existing regex fallback that ships behind the feature flag.
 */

/**
 * User wants an existing/new practice timeline to gain a drill or in-practice
 * activity/game segment (not an opponent/game on the calendar).
 */
export function isAddDrillToPracticeIntent(text: string): boolean {
  const lower = text.toLowerCase().trim();

  const addVerb =
    /\b(add|include|put|throw\s+in|slip\s+in|work\s+in)\b/i.test(lower) ||
    /\b(?:can\s+you|could\s+you)\s+add\b/i.test(lower) ||
    /\bplease\s+add\b/i.test(lower) ||
    /\bwant\s+(?:you\s+)?to\s+add\b/i.test(lower);

  if (!addVerb) return false;

  const practiceRef =
    /\bpractice'?s?\b|\bpractice\b|\bsession\b/i.test(lower);
  const hasDrill = /\bdrill(?:s|es)?\b/i.test(lower);
  const activityWithPractice =
    practiceRef &&
    /\b(fun\s+|coaching\s+|team\s+|mini\s+)?(games?|relay|relay\s+race|scrimmage|activity|challenge|competition|play)\b/i.test(
      lower
    );
  const contentCue =
    hasDrill ||
    activityWithPractice ||
    (practiceRef &&
      /\b(last|final)\s+\d+\s*(?:min|minutes?)|\bend\s+of\b|\bhalf\s*time\b|\bwater\s*break\b/i.test(
        lower
      ));

  if (!contentCue) return false;

  const hasRelativeDate =
    /\b(?:tomorrows?|tomorrow'?s)\b|\b(today|tonight)\b|\bnext\s+/i.test(lower) ||
    /\b(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/i.test(lower);
  const hasIso = /\b20\d{2}-\d{2}-\d{2}\b/.test(lower);
  const hasTimeSegment =
    /\b(last|final)\s+\d+\s*(?:min|minutes?)\b/i.test(lower) || /\bend\s+of\b/i.test(lower);

  if (!practiceRef && !hasRelativeDate && !hasIso && !hasTimeSegment) return false;

  if (/\badd\s+(?:a\s+)?game\b.*\b(schedule|opponent|vs\.?)\b/i.test(lower)) return false;

  if (/\b(create|make|generate|build)\s+(me\s+)?(a\s+)?practice(\s+plan)?\s+for\b/i.test(lower)) {
    return false;
  }

  return true;
}

/**
 * When we're waiting on a player's name but the coach clearly pivoted to
 * practice/drill/game-on-practice language, abandon the roster flow.
 */
export function supersedesPendingPlayerCreateWithPracticeRequest(text: string): boolean {
  return isAddDrillToPracticeIntent(text) || isPracticePlanningClarificationAfterFalsePlayerRoute(text);
}

function isPracticePlanningClarificationAfterFalsePlayerRoute(text: string): boolean {
  const lower = text.toLowerCase();
  return (
    /\b(no|not|actually|wrong)\b/i.test(text) &&
    /\b(practice|drill|session|kids|team)\b/i.test(lower) &&
    /\b(add|want|instead|rather)\b/i.test(lower)
  );
}

/** True only when the user clearly asks to create a practice (not just mentions "practice"). */
export function isCreatePracticeIntent(text: string): boolean {
  const lower = text.toLowerCase().trim();
  const createPatterns = [
    /^(create|make|generate|build)\s+(me\s+)?(a\s+)?practice(\s+plan)?/i,
    /^i\s+(want|need)\s+(to\s+create\s+)?(a\s+)?practice(\s+plan)?/i,
    /^set\s+up\s+(a\s+)?practice(\s+plan)?/i,
    /^(can you\s+)?(please\s+)?create\s+(a\s+)?practice(\s+plan)?/i,
    /^create\s+(a\s+)?practice(\s+plan)?\s/,
  ];
  return createPatterns.some((re) => re.test(lower));
}

/** True only when the user clearly asks to create a game plan (not a game/schedule). */
export function isCreateGamePlanIntent(text: string): boolean {
  const lower = text.toLowerCase().trim();
  const createPatterns = [
    /^(create|make|generate|build)\s+(me\s+)?(a\s+)?game\s+plan/i,
    /^i\s+(want|need)\s+(to\s+create\s+)?(a\s+)?game\s+plan/i,
    /^(can you\s+)?(please\s+)?create\s+(a\s+)?game\s+plan/i,
    /^create\s+(a\s+)?game\s+plan\s/,
  ];
  return createPatterns.some((re) => re.test(lower));
}

/** True when the user asks to create/save a formation (depth chart). */
export function isCreateFormationIntent(text: string): boolean {
  const lower = text.toLowerCase().trim();
  const patterns = [
    /\b(create|make|save|add)\s+(?:a\s+)?(?:new\s+)?formation\b/i,
    /\b(create|make|save)\s+(?:the|that|this)\s+formation\b/i,
    /^(?:yes\s+)?(?:please\s+)?create\s+(?:the|that|this)\s+formation/i,
    /\b(?:can you\s+)?(?:please\s+)?(create|save)\s+(?:the|that|this)\s+formation/i,
  ];
  return patterns.some((re) => re.test(lower));
}

/** True when the user asks to assign or move a player to a position (with or without formation). */
export function isAssignPlayerToFormationIntent(text: string): boolean {
  // Injury intents win — "set Cole to healthy" must not be mis-routed here.
  if (isSetInjuryIntent(text)) return false;
  const lower = text.toLowerCase().trim();
  const patterns = [
    // With formation: "put X at Y in Spread", "assign #5 to RB in the Spread formation", "move Ace to LDT in 4-3"
    /\b(put|assign|set|move)\s+.+\s+(?:at|to|as)\s+.+\s+(?:in|for)\s+(?:the\s+)?.+\s*(?:formation)?\s*$/i,
    /\b(?:assign|put|move)\s+.+\s+(?:at|to)\s+.+\s+in\s+/i,
    // Without formation: "assign Ace to the LDT position", "put Ace at LDT position", "move Ace to LDT"
    /\b(assign|put|set|move)\s+.+\s+(?:to|at|as)\s+(?:the\s+)?.+\s*(?:position)?\s*$/i,
  ];
  return patterns.some((re) => re.test(lower));
}

/**
 * Baseball/softball game-scoped lineup & fielding, or sport-agnostic phrasing that still maps to the depth-chart agent.
 * Kept separate from formation-assignment so we can route "pitching rotation" without the word "formation".
 */
export function isGameLineupCoachIntent(text: string): boolean {
  if (isSetInjuryIntent(text)) return false;
  if (isCreateGameIntent(text) || isCreateGamePlanIntent(text)) return false;
  if (isAddDrillToPracticeIntent(text)) return false;
  const lower = text.toLowerCase().trim();
  if (/\bgame\s+plan\b/.test(lower)) return false;
  if (/\b(create|add|schedule)\s+(?:a\s+)?game\b/.test(lower)) return false;

  const patterns = [
    /\b(pitching\s+(?:rotation|order)|starting\s+pitch(?:er|ers)?|on\s+the\s+mound|who(?:'s|\s+is)\s+pitching)\b/,
    /\b(batting\s+order|game\s+lineup)\b/,
    /\b(set|update)\s+(?:the\s+)?(?:batting\s+)?lineup\b/,
    /\brelieved\s+by\b/,
    /\b(line\s+change|shift\s+change)\b/,
    /\b(first|next|last)\s+(one|two|three|four|five|six|seven|\d+)\s+innings?\b/,
    // "put X in the 10th spot", "move X up/down to 7", "Nth spot of the lineup"
    /\b\d+(st|nd|rd|th)\s+(spot|slot|position)\b/,
    /\bspot\s+(of\s+the\s+)?lineup\b/,
    /\bmove\s+\S+\s+(up|down)\s+to\s+\d+\b/,
    /\blineup\s+(spot|position|slot)\b/,
    /\b(put|move|slot)\s+.+\s+in(to)?\s+(?:the\s+)?\d+(st|nd|rd|th)\s+(spot|slot|position|batting)\b/,
  ];
  return patterns.some((re) => re.test(lower));
}

/** True when the user asks to create/add/schedule a game (schedule entry), not a game plan. */
export function isCreateGameIntent(text: string): boolean {
  const lower = text.toLowerCase().trim();
  if (/\bgame\s+plan\b/.test(lower)) return false;
  const patterns = [
    /^(create|add|schedule)\s+(a\s+)?game\b/i,
    /^(can you\s+)?(please\s+)?(create|add|schedule)\s+(a\s+)?game\b/i,
    /^i\s+(want|need)\s+to\s+(create|add|schedule)\s+(a\s+)?game\b/i,
    /\bcreate\s+(a\s+)?game\s*[.?!]?\s*$/i,
    /\badd\s+(a\s+)?game\s*[.?!]?\s*$/i,
    /\bschedule\s+(a\s+)?game\b/i,
    /\badd\s+(a\s+)?game\b/i,
    /\b(?:i'd|i\s+would)\s+like\s+to\s+add\s+(a\s+)?game\b/i,
    /\b(?:i\s+want|i\s+need)\s+to\s+add\s+(a\s+)?game\b/i,
    /\bgame\s+to\s+(?:the\s+)?schedule\b/i,
    /\badd\s+(a\s+)?game\s+to\s+(?:the\s+)?schedule\b/i,
  ];
  return patterns.some((re) => re.test(lower));
}

/**
 * True when the user asks to add/create a player so we run the real add
 * flow instead of the general AI. Excludes game, game plan, formation,
 * practice, and drill-related phrasings.
 */
export function isCreatePlayerIntent(text: string): boolean {
  const lower = text.toLowerCase().trim();
  if (isAddDrillToPracticeIntent(text)) return false;
  // Don't treat other "create" targets as adding a player
  if (/\bdrill(s)?\b/.test(lower)) return false;
  if (/\bpractice\s+plan\b/.test(lower)) return false;
  if (/\badd\s+(a\s+)?game\b/.test(lower) || /\bgame\s+to\s+(?:the\s+)?schedule\b/.test(lower)) return false;
  if (/\bformation\b/.test(lower)) return false;
  if (/\bgame\s+plan\b/.test(lower)) return false;
  if (/\b(create|make|add)\s+(?:a\s+)?practice\b/.test(lower)) return false;
  if (/\b(create|make|add)\s+(?:a\s+)?game\b/.test(lower)) return false;
  // Loose "please/can you add …" belongs to drills/practice-content if practice/drill/session appear
  if (/\b(practice|session|drill)\b/.test(lower) && /\b(?:can\s+you|could\s+you|please)\s+add\b/i.test(lower)) {
    return false;
  }
  if (/\bwant\s+(?:you\s+)?to\s+add\b/i.test(lower) && /\b(practice|session|drill)\b/i.test(lower)) {
    return false;
  }
  const patterns = [
    /\badd\s+(?:a\s+)?player\b/i,
    /\bcreate\s+(?:a\s+)?player\b/i,
    /\bnew\s+player\b/i,
    /\badd\s+.+\s+to\s+(?:the\s+)?roster\b/i,
    /\bput\s+.+\s+on\s+(?:the\s+)?roster\b/i,
    /\b(?:i'd|i\s+would)\s+like\s+to\s+add\s+(?:a\s+)?player\b/i,
    /\b(?:i'd|i\s+would)\s+like\s+to\s+add\s+[A-Za-z][A-Za-z'\-]+\s+[A-Za-z][A-Za-z'\-]+/i,
    /\b(?:i\s+want\s+to|i\s+need\s+to)\s+add\s+(?:a\s+)?player\b/i,
    /\b(?:i\s+want\s+to|i\s+need\s+to)\s+add\s+[A-Za-z][A-Za-z'\-]+\s+[A-Za-z][A-Za-z'\-]+/i,
    /\bplease\s+add\s+(?:a\s+)?player\b/i,
    /\bplease\s+add\s+[A-Za-z][A-Za-z'\-]+\s+[A-Za-z][A-Za-z'\-]+/i,
    /\b(?:can\s+you|could\s+you)\s+add\s+(?:a\s+)?player\b/i,
    /\b(?:can\s+you|could\s+you)\s+add\s+[A-Za-z][A-Za-z'\-]+\s+[A-Za-z][A-Za-z'\-]+/i,
    // "add John Doe" — two name-like words (excludes formation via formation check above)
    /\badd\s+[A-Za-z][A-Za-z'\-]+\s+[A-Za-z][A-Za-z'\-]+/i,
  ];
  return patterns.some((re) => re.test(lower));
}

/** True when the user asks to set or update a player's injury status. Kept flexible for natural phrasing. */
export function isSetInjuryIntent(text: string): boolean {
  const lower = text.toLowerCase().trim();
  const patterns = [
    /\bmark\s+.+\s+(?:as\s+)?(?:out|questionable|doubtful|healthy)\b/i,
    /\bset\s+.+\s+(?:as|to)\s+(?:out|questionable|doubtful|healthy)\b/i,
    /\b(?:out|questionable|doubtful|healthy)\s+for\s+.+/i,
    /\binjury\s+(?:status\s+)?(?:for|on)\s+.+/i,
    /\bset\s+injury\b/i,
    /\bupdate\s+injury\b/i,
    /\b(?:#?\d+|[A-Za-z][A-Za-z'\-\s]+)\s+is\s+(?:out|questionable|doubtful|healthy)\b/i,
    /\b(?:#?\d+|[A-Za-z][A-Za-z'\-\s]+)\s+out\b/i,
    // Natural phrasing: "Cole is sick", "Finn is injured", "X should be marked out"
    /\bis\s+(?:sick|injured|hurt|unavailable)\b/i,
    /\bshould\s+be\s+marked\s+(?:out|down)\b/i,
    /\b(?:sick|injured|hurt)\s+and\s+should\s+be\s+marked\s+out\b/i,
    // Availability phrasing
    /\bavailability\s+status\b/i,
    /\bset\s+availability\b/i,
    /\bmake\s+.+\s+available\b/i,
    /\bactivate\s+.+\b/i,
  ];
  return patterns.some((re) => re.test(lower));
}
